import { getClient } from '@api/client';
import { graphql } from '@api/generated';

export const getPcsConversationDetailsQuery = graphql(`
  query getConversationDetails($kazooCallId: String!) {
    conversationDetails(kazooCallId: $kazooCallId) {
      hasTransferSession
      transferredByUserId
      sessionOfferedAt
      to
      from
    }
  }
`);

export type PcsConversationDetailsResult = {
  isTransfer: boolean;
  transferredByUserId?: string | null;
  sessionOfferedAt?: string | null;
  to?: string | null;
  from?: string | null;
};

/**
 * Queries for PCS conversation details by kazooCallId.
 *
 * This replaces the deprecated transferDetails query.
 * Returns transfer information including who transferred the call and when the session was offered.
 *
 * Errors should be handled by the caller.
 * @param kazooCallId
 * @returns PcsConversationDetailsResult
 */
async function getPcsConversationDetails(
  kazooCallId: string,
): Promise<PcsConversationDetailsResult> {
  const { data, error } = await getClient().query({
    query: getPcsConversationDetailsQuery,
    variables: { kazooCallId },
  });

  if (error) {
    throw new Error(error.message);
  }

  const conversationDetails = data?.conversationDetails;

  if (!conversationDetails) {
    return {
      isTransfer: false,
      transferredByUserId: null,
      sessionOfferedAt: null,
      to: null,
      from: null,
    };
  }

  // Use the hasTransferSession field to determine if this is a transfer
  return {
    isTransfer: conversationDetails.hasTransferSession,
    transferredByUserId: conversationDetails.transferredByUserId,
    sessionOfferedAt: conversationDetails.sessionOfferedAt,
    to: conversationDetails.to,
    from: conversationDetails.from,
  };
}

export default getPcsConversationDetails;
